import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageSEO from '@/components/feature/PageSEO';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于番茄视频',
    description: '番茄视频是一个专业的免费影视资源聚合平台，致力于为用户提供最优质的在线观影体验。',
    url: 'https://www.fanqieshipin.com/about',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '番茄视频是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '番茄视频是一个专业的免费影视资源聚合平台，汇集西瓜视频免费、91在线视频、小蓝视频、久久视频等主流平台的海量影视资源，致力于为用户提供一站式的免费高清观影体验。',
        },
      },
      {
        '@type': 'Question',
        name: '番茄视频需要付费吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '完全不需要。番茄视频所有内容均为免费在线观看，无需注册会员，无需支付任何费用。',
        },
      },
      {
        '@type': 'Question',
        name: '番茄视频支持哪些设备？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '番茄视频支持电脑、平板、手机等各类设备，只需打开浏览器即可随时随地畅享高清影视。',
        },
      },
    ],
  };

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '关于我们' },
  ];

  return (
    <>
      <PageSEO
        title="关于我们 - 免费高清影视聚合平台"
        description="番茄视频是一个专业的免费影视资源聚合平台，汇集西瓜视频免费、91在线视频、小蓝视频、久久视频等平台资源，提供电影电视剧全集免费在线观看。"
        keywords="番茄视频,关于我们,免费影视,高清影视,品牌介绍,联系方式"
        canonicalUrl="/about"
        schemaData={[aboutSchema, faqSchema]}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="max-w-[1000px] mx-auto px-4 md:px-6 pt-6 pb-12">
          <Breadcrumb items={breadcrumbItems} />

          {/* Brand Introduction */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary-500 flex items-center justify-center">
                <i className="ri-play-fill text-white text-2xl"></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                  关于番茄视频
                </h1>
                <p className="text-sm text-foreground-500">专业的免费影视资源聚合平台</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="rounded-xl overflow-hidden bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20media%20streaming%20office%20interior%20with%20large%20screens%20displaying%20movie%20posters%2C%20clean%20warm%20lighting%2C%20creative%20workspace%20vibe%2C%20professional%20yet%20cozy%20atmosphere%2C%20no%20people%2C%20cinematic%20color%20grading%20with%20warm%20red%20accents&width=800&height=500&seq=about-brand-01&orientation=landscape"
                  alt="番茄视频品牌形象"
                  className="w-full h-full object-cover"
                  title="番茄视频品牌形象"
                />
              </div>
              <div className="flex flex-col justify-center">
                <h2 className="text-xl font-bold font-heading text-foreground-900 mb-3">
                  品牌介绍
                </h2>
                <p className="text-foreground-700 leading-relaxed mb-3">
                  番茄视频是一个专注于<strong className="text-foreground-900">免费高清影视资源聚合</strong>的专业平台。我们汇集了
                  <strong className="text-foreground-900">西瓜视频免费</strong>、<strong className="text-foreground-900">91在线视频</strong>、
                  <strong className="text-foreground-900">小蓝视频</strong>、<strong className="text-foreground-900">久久视频</strong>等
                  多家主流视频平台的优质资源，为用户提供<strong className="text-foreground-900">一站式的免费观影体验</strong>。
                </p>
                <p className="text-foreground-700 leading-relaxed">
                  我们的使命是让每一位用户都能轻松享受到<strong className="text-foreground-900">高清流畅的影视内容</strong>，无需付费，无需复杂的注册流程，即点即播，畅享影视盛宴。
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: 'ri-eye-line', title: '海量资源', desc: '10,000+影视作品持续更新' },
                { icon: 'ri-shield-check-line', title: '安全可靠', desc: '绿色观影环境，隐私有保障' },
                { icon: 'ri-flashlight-line', title: '极速播放', desc: '即点即播，告别缓冲等待' },
              ].map((item) => (
                <div key={item.title} className="p-5 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <div className="w-12 h-12 rounded-lg bg-accent-100 flex items-center justify-center mx-auto mb-3">
                    <i className={`${item.icon} text-accent-600 text-xl`}></i>
                  </div>
                  <h3 className="font-semibold text-foreground-900 mb-1">{item.title}</h3>
                  <p className="text-sm text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* User Reputation */}
          <section className="mb-12">
            <h2 className="text-xl font-bold font-heading text-foreground-900 mb-6 flex items-center gap-2">
              <div className="w-1 h-6 rounded-full bg-accent-500"></div>
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  name: '张先生',
                  role: '影视爱好者',
                  comment: '番茄视频真的太方便了！一个网站就能看到西瓜视频免费、91在线视频等多个平台的内容，画质清晰播放流畅，推荐给所有爱看电影的朋友。',
                  rating: 5,
                },
                {
                  name: '李女士',
                  role: '追剧达人',
                  comment: '每天必刷番茄视频追剧，更新速度和各大平台同步，而且完全免费不用付费。高清画质看着就是舒服，强烈推荐！',
                  rating: 5,
                },
                {
                  name: '王同学',
                  role: '在校大学生',
                  comment: '作为学生党，免费观影平台就是我的最爱。番茄视频资源全、不卡顿，还能在小蓝视频和久久视频之间自由切换，体验超棒。',
                  rating: 5,
                },
              ].map((review) => (
                <div
                  key={review.name}
                  className="p-5 rounded-lg bg-background-50 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                    &ldquo;{review.comment}&rdquo;
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center text-secondary-700 text-xs font-bold">
                      {review.name[0]}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{review.name}</p>
                      <p className="text-xs text-foreground-500">{review.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="mb-12">
            <h2 className="text-xl font-bold font-heading text-foreground-900 mb-6 flex items-center gap-2">
              <div className="w-1 h-6 rounded-full bg-secondary-500"></div>
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 rounded-lg bg-background-100 border border-background-200/70">
                <h3 className="font-semibold text-foreground-900 mb-4">联系方式</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">电子邮箱</p>
                      <p className="text-sm text-foreground-900">contact@fanqieshipin.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-customer-service-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">客服热线</p>
                      <p className="text-sm text-foreground-900">400-888-8888</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-secondary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">工作时间</p>
                      <p className="text-sm text-foreground-900">周一至周日 9:00 - 21:00</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-lg bg-background-100 border border-background-200/70">
                <h3 className="font-semibold text-foreground-900 mb-4">商务合作</h3>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  如果您是内容提供方或有商务合作意向，欢迎通过以下方式联系我们。番茄视频期待与更多优质平台携手合作，共同为用户提供更丰富的影视内容。
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-briefcase-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">商务邮箱</p>
                      <p className="text-sm text-foreground-900">business@fanqieshipin.com</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Partner Platforms */}
          <section>
            <h2 className="text-xl font-bold font-heading text-foreground-900 mb-6 flex items-center gap-2">
              <div className="w-1 h-6 rounded-full bg-primary-500"></div>
              合作伙伴
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { name: '西瓜视频免费', icon: 'ri-video-line' },
                { name: '91在线视频', icon: 'ri-play-circle-line' },
                { name: '小蓝视频', icon: 'ri-live-line' },
                { name: '久久视频', icon: 'ri-film-line' },
              ].map((partner) => (
                <div
                  key={partner.name}
                  className="p-5 rounded-lg bg-background-50 border border-background-200/70 text-center hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <div className="w-12 h-12 rounded-lg bg-accent-100 flex items-center justify-center mx-auto mb-3">
                    <i className={`${partner.icon} text-accent-600 text-xl`}></i>
                  </div>
                  <p className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{partner.name}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}